import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { EventModel } from '@app/events/shared/event.model';
import { EventTimelineItemModel } from '@app/events/shared/event-timeline-item.model';
import { EventTemplateService, TemplateFromEventData } from '@app/event-templates/shared/event-template.service';
import { NotificationService } from '@app/shared/notification/notification.service';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, Calendar, Clock } from 'lucide-angular';

@Component({
  selector: 'app-create-template-from-event',
  templateUrl: './create-template-from-event.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    TextInputComponent,
    TextareaComponent,
    SelectComponent,
    LucideAngularModule,
  ],
})
export class CreateTemplateFromEventComponent implements OnInit {
  @Input() event!: EventModel;
  @Input() timelineItems: EventTimelineItemModel[] = [];
  @Output() closeModal = new EventEmitter<void>();
  @Output() templateCreated = new EventEmitter<void>();

  protected readonly CalendarIcon = Calendar;
  protected readonly ClockIcon = Clock;

  protected processing = false;
  protected selectedItemIds: Set<number> = new Set();
  protected eventTypes: Array<{ value: string; label: string }> = [];

  protected form = new FormGroup({
    template_name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    template_description: new FormControl(''),
    event_type: new FormControl('', [Validators.required]),
  });

  constructor(
    private eventTemplateService: EventTemplateService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.initializeEventTypes();
    this.initializeForm();
  }

  private initializeEventTypes(): void {
    this.eventTypes = this.eventTemplateService.getEventTypes().map((type) => ({
      value: type,
      label: type,
    }));
  }

  private initializeForm(): void {
    if (this.event) {
      // Pre-fill template name based on event
      this.form.patchValue({
        template_name: `${this.event.name} Template`,
        event_type: this.event.category || '',
      });
    }
  }

  protected toggleTimelineItem(itemId: number): void {
    if (this.selectedItemIds.has(itemId)) {
      this.selectedItemIds.delete(itemId);
    } else {
      this.selectedItemIds.add(itemId);
    }
  }

  protected isItemSelected(itemId: number): boolean {
    return this.selectedItemIds.has(itemId);
  }

  protected get selectedItems(): EventTimelineItemModel[] {
    return this.timelineItems.filter((item) => this.selectedItemIds.has(item.id));
  }

  protected get hasSelectedItems(): boolean {
    return this.selectedItemIds.size > 0;
  }

  protected selectAllItems(): void {
    this.timelineItems.forEach((item) => this.selectedItemIds.add(item.id));
  }

  protected deselectAllItems(): void {
    this.selectedItemIds.clear();
  }

  protected calculateDaysOffset(itemDate: Date): number {
    if (!this.event.start_date || !itemDate) return 0;

    const eventStart = new Date(this.event.start_date);
    const itemDateTime = new Date(itemDate);

    // Use startOfDay for consistent calculation with backend
    eventStart.setHours(0, 0, 0, 0);
    itemDateTime.setHours(0, 0, 0, 0);

    // Calculate difference in days
    const timeDiff = itemDateTime.getTime() - eventStart.getTime();
    return Math.floor(timeDiff / (1000 * 3600 * 24));
  }

  protected formatTime(datetime: Date): string {
    return new Date(datetime).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  protected onSubmit(): void {
    if (this.form.invalid || !this.hasSelectedItems || this.processing) {
      return;
    }

    if (!this.event.start_date) {
      this.notificationService.error('Event must have a start date to create template.');
      return;
    }

    this.processing = true;

    const formData: TemplateFromEventData = {
      template_name: this.form.value.template_name!,
      event_type: this.form.value.event_type! as TemplateFromEventData['event_type'],
      timeline_item_ids: Array.from(this.selectedItemIds),
    };

    // Only include template_description if it has a value
    if (this.form.value.template_description?.trim()) {
      formData.template_description = this.form.value.template_description.trim();
    }

    this.eventTemplateService.createFromEvent(this.event.id, formData).subscribe({
      next: (response) => {
        this.notificationService.success(response.message);
        this.templateCreated.emit();
        this.closeModal.emit();
        this.resetForm();
      },
      error: (error) => {
        console.error('Error creating template:', error);
        this.notificationService.error(error.error?.message || 'Failed to create template from event.');
      },
      complete: () => {
        this.processing = false;
      },
    });
  }

  protected onCancel(): void {
    this.closeModal.emit();
    this.resetForm();
  }

  private resetForm(): void {
    this.form.reset();
    this.selectedItemIds.clear();
    this.processing = false;
  }

  // Getters for form controls
  protected get templateNameControl(): FormControl {
    return this.form.get('template_name') as FormControl;
  }

  protected get templateDescriptionControl(): FormControl {
    return this.form.get('template_description') as FormControl;
  }

  protected get eventTypeControl(): FormControl {
    return this.form.get('event_type') as FormControl;
  }
}
